import { useEffect, type ReactNode } from 'react';

import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';

import { getAppConfig } from '../../lib/config';

import { BrandedShell } from './BrandedShell';
import { msalInstance, hubLoginRequest } from './msalConfig';

function AuthResolver({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (isAuthenticated || accounts.length > 0) {
      return;
    }

    // Try a silent, prompt-free login first — if the browser already has an
    // active Entra session (e.g. the user is signed into Outlook/Teams),
    // this resolves instantly with no redirect at all.
    instance.ssoSilent(hubLoginRequest).catch(() => {
      instance.loginRedirect(hubLoginRequest);
    });
  }, [instance, isAuthenticated, accounts.length]);

  if (!isAuthenticated) {
    return (
      <BrandedShell title="Sign in">
        <p>Signing you in with your SS&amp;C account…</p>
      </BrandedShell>
    );
  }

  return <>{children}</>;
}

export function AuthGate({ children }: { children: ReactNode }) {
  if (getAppConfig().demoMode) {
    console.warn(
      '[unified-login-hub] DEMO_MODE is enabled — skipping Entra sign-in entirely. Do not use this outside local/demo environments.',
    );

    return <>{children}</>;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthResolver>{children}</AuthResolver>
    </MsalProvider>
  );
}
